import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Play, Pause, RotateCcw, ChevronRight, 
  ImageIcon, Sparkles, CheckCircle2
} from 'lucide-react';

/**
 * GifDemoCard - Displays an animated GIF demo placeholder
 * Shows step-by-step feature demonstrations
 */
export function GifDemoCard({ 
  demo, 
  size = 'medium', // 'small' | 'medium' | 'large'
  showSteps = true 
}) {
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);

  const sizeClasses = {
    small: 'w-32 h-24',
    medium: 'w-48 h-36',
    large: 'w-64 h-48'
  };

  const handleReplay = () => {
    setCurrentStep(0);
    setIsPlaying(true);
  };

  return (
    <Card 
      className="overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`gif-demo-${demo.id}`}
    >
      <div className="flex">
        {/* GIF placeholder */}
        <div className={`${sizeClasses[size]} bg-gradient-to-br from-gray-100 to-gray-200 relative flex-shrink-0`}>
          {demo.gifUrl ? (
            <img 
              src={demo.gifUrl} 
              alt={demo.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center mb-2">
                <Sparkles className="w-6 h-6 text-emerald-500" />
              </div>
              <p className="text-xs text-gray-500 text-center px-2">Animated Demo</p>
            </div>
          )}

          {/* Play/Pause overlay */}
          <button
            className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 hover:opacity-100"
            onClick={() => setIsPlaying(!isPlaying)}
          >
            {isPlaying ? (
              <Pause className="w-8 h-8 text-white drop-shadow-lg" />
            ) : (
              <Play className="w-8 h-8 text-white drop-shadow-lg" />
            )}
          </button>

          {/* Duration badge */}
          <Badge className="absolute bottom-1 right-1 bg-black/60 text-white text-xs">
            {demo.duration}
          </Badge>
        </div>

        {/* Content */}
        <CardContent className="flex-1 p-3">
          <h4 className="font-medium text-sm text-gray-900 mb-1">{demo.title}</h4>
          <p className="text-xs text-gray-500 mb-2">{demo.description}</p>

          {/* Steps */}
          {showSteps && demo.steps && demo.steps.length > 0 && (
            <div className="space-y-1">
              {demo.steps.map((step, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center gap-2 text-xs ${
                    idx === currentStep ? 'text-emerald-600 font-medium' : 'text-gray-500'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                    idx < currentStep 
                      ? 'bg-emerald-500 text-white' 
                      : idx === currentStep 
                        ? 'bg-emerald-100 text-emerald-600 ring-2 ring-emerald-500'
                        : 'bg-gray-200 text-gray-500'
                  }`}>
                    {idx < currentStep ? <CheckCircle2 className="w-3 h-3" /> : idx + 1}
                  </div>
                  <span>{step}</span>
                </div>
              ))}
            </div>
          )}

          {/* Replay button */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="mt-2 h-7 text-xs"
            onClick={handleReplay}
          >
            <RotateCcw className="w-3 h-3 mr-1" />
            Replay
          </Button>
        </CardContent>
      </div>
    </Card>
  );
}

/**
 * GifDemoGrid - Display multiple GIF demos in a grid
 */
export function GifDemoGrid({ demos, columns = 2 }) {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-4`}>
      {demos.map((demo) => (
        <GifDemoCard key={demo.id} demo={demo} />
      ))}
    </div>
  );
}

/**
 * GifDemoInline - Smaller inline version for use within documentation
 */
export function GifDemoInline({ demo }) {
  return (
    <div 
      className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 cursor-pointer transition-colors"
      data-testid={`gif-inline-${demo.id}`}
    >
      <div className="w-10 h-10 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-lg flex items-center justify-center">
        <Play className="w-4 h-4 text-emerald-600" />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-900">{demo.title}</p>
        <p className="text-xs text-gray-500">{demo.duration} demo</p>
      </div>
    </div>
  );
}

export default GifDemoCard;
